import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BotConfiguration } from '@/components/developer/sections/BotConfiguration';
import { EnvironmentVariables } from '@/components/developer/sections/EnvironmentVariables';
import { LogViewer } from '@/components/developer/shared/LogViewer';
import { BotMetricsDetail } from '@/components/developer/sections/BotMetricsDetail';

interface BotDetailPanelProps {
  botId: string;
  onClose: () => void;
}

// Mock hook to get bot details
const useBot = (botId: string) => ({
  bot: {
    id: botId,
    name: 'Lead Qualification Bot',
    version: 'v2.1.3',
    status: 'healthy',
    environment: 'production',
    description: 'Automated lead qualification and initial contact bot',
    repository: 'https://github.com/company/lead-qualification-bot',
    lastDeploy: '2024-01-15T10:30:00Z',
    uptime: 99.8,
    memory: 324,
    cpu: 45,
    requests: 12847
  },
  loading: false,
  error: null
});

export function BotDetailPanel({ botId, onClose }: BotDetailPanelProps) {
  const { bot } = useBot(botId);
  
  return (
    <Sheet open={true} onOpenChange={onClose}>
      <SheetContent className="w-[600px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{bot.name}</SheetTitle>
          <SheetDescription>
            Bot ID: <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">{bot.id}</code>
            {bot.version && (
              <>
                {' • '}Version: <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">{bot.version}</code>
              </>
            )}
          </SheetDescription>
        </SheetHeader>
        
        <div className="mt-6 space-y-6">
          {/* Bot Configuration */}
          <Tabs defaultValue="config">
            <TabsList className="grid grid-cols-4 w-full">
              <TabsTrigger value="config">Config</TabsTrigger>
              <TabsTrigger value="env">Environment</TabsTrigger>
              <TabsTrigger value="logs">Logs</TabsTrigger>
              <TabsTrigger value="metrics">Metrics</TabsTrigger>
            </TabsList>
            
            <TabsContent value="config" className="mt-4">
              <BotConfiguration bot={bot} />
            </TabsContent>
            
            <TabsContent value="env" className="mt-4">
              <EnvironmentVariables botId={bot.id} />
            </TabsContent>
            
            <TabsContent value="logs" className="mt-4">
              <LogViewer 
                source={`bot-${bot.id}`}
                height={400}
                follow={true}
              />
            </TabsContent>
            
            <TabsContent value="metrics" className="mt-4">
              <BotMetricsDetail bot={bot} />
            </TabsContent>
          </Tabs>
        </div>
      </SheetContent>
    </Sheet>
  );
}